import { Card, Table, useSearchParams, useEffect, useRef } from 'external/react';
import useAccounting from 'context/accounting-context/hook';

import { ROW_HEIGHT } from './PayoutsTableProperties';
import PayoutsTableHeader from './PayoutsTableHeader';
import PayoutsTableBody from './PayoutsTableBody';
import PayoutsTableToolbar from './PayoutsTableToolbar';
import ClientsTablePaging from './PayoutsTablePaging';

const PayoutsTable = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { payoutsPaging } = useAccounting();

  const toolbarRef = useRef<HTMLDivElement>(null);
  const theadRef = useRef<HTMLTableSectionElement>(null);
  const pagingRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);

  const updateParams = (updates: Record<string, string | undefined>) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      Object.entries(updates).forEach(([key, value]) => {
        if (value === undefined || value === '') next.delete(key);
        else next.set(key, value);
      });
      return next;
    });
  };

  const calculate = () => {
    const toolbarH = toolbarRef.current?.offsetHeight ?? 0;
    const theadH = theadRef.current?.offsetHeight ?? 0;
    const pagingH = pagingRef.current?.offsetHeight ?? 0;
    const cardPadding = (16 * 2) + 40 + (48 * 2);
    const chrome = toolbarH + theadH + pagingH + cardPadding;
    const available = window.innerHeight - (cardRef.current?.getBoundingClientRect().top ?? 0) - chrome;
    const rows = Math.max(1, Math.floor(available / ROW_HEIGHT));
    updateParams({ pageSize: String(Math.min(rows, 50)), page: '1' });
  };

  useEffect(() => {
    calculate();
    window.addEventListener('resize', calculate);
    return () => window.removeEventListener('resize', calculate);
  }, []);

  const pattern = searchParams.get('pattern') ?? undefined;
  const sortBy = searchParams.get('sortBy') ?? undefined;

  const handleSearch = (pattern: string | undefined) => updateParams({ pattern, page: '1' });
  const handleSort = (sortBy: string) => updateParams({ sortBy: sortBy || undefined, page: '1' });
  const handlePageChange = (page: number) => updateParams({ page: String(page) });

  const noResults = payoutsPaging && payoutsPaging.recordCount === 0;
  const from = searchParams.get('from') ?? undefined;
  const to = searchParams.get('to') ?? undefined;

  const handleDateRange = (from: string | undefined, to: string | undefined) => updateParams({ from, to, page: '1' });

  return (
    <div ref={cardRef}>
      <Card.Body>
        <div ref={toolbarRef}>
          <PayoutsTableToolbar pattern={pattern} from={from} to={to} onSearch={handleSearch} onDateRange={handleDateRange} />
        </div>

        {noResults ? (
          <div
            className="d-flex flex-column align-items-center justify-content-center text-muted"
            style={{ height: `${ROW_HEIGHT * 5}px` }}
          >
            <span style={{ fontSize: '2rem' }}>🔍</span>
            <p className="mt-2 mb-0">
              No se encontraron pagos{pattern ? ` para "${pattern}"` : ''}.
            </p>
          </div>
        ) : (
          <Table bordered hover style={{ tableLayout: 'fixed', width: '100%' }}>
            <thead ref={theadRef}>
              <PayoutsTableHeader sortBy={sortBy} onSort={handleSort} />
            </thead>
            <PayoutsTableBody />
          </Table>
        )}

        <div ref={pagingRef}>
          {payoutsPaging && payoutsPaging.recordCount > 0 && (
            <ClientsTablePaging {...payoutsPaging} onPageChange={handlePageChange} />
          )}
        </div>
      </Card.Body>
    </div>
  );
};

export default PayoutsTable;